import {
  FC,
  FocusEvent,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  colorBorderDisabled,
  colorBorderSecondary,
  colorLink,
  colorPrimary,
  colorTextQuaternary,
  fontSize,
  inputPaddingBlock,
  inputPaddingBlockLG,
  inputPaddingBlockSM,
  inputPaddingInline,
  inputPaddingInlineLG,
  inputPaddingInlineSM,
  lineHeight,
  token,
} from '@atlas-frontend/design-tokens';
import { TextEllipsis } from '../../components';
import { listenFocusFormInput } from '../events/FocusFormInputEvent';
import { FORM_DATE_PICKER_POPUP_CLASSNAME } from './constants';
import { SizeType } from 'antd/es/config-provider/SizeContext';

export interface FormInputPreviewProps extends HTMLAttributes<HTMLDivElement> {
  name: string;
  id: string;
  enabled?: boolean;
  ellipsis?: boolean;
  render?: (value: any) => ReactNode;
  size?: SizeType;
  previewOnly?: boolean;
  disabled?: boolean;
}

export const FormInputPreview: FC<FormInputPreviewProps> = (props) => {
  const {
    children,
    name,
    id,
    enabled,
    ellipsis = true,
    render,
    size = 'middle',
    previewOnly = false,
    disabled = false,
    className,
    ...rest
  } = props;

  const classNames = ['input-preview'];

  if (className) {
    classNames.push(className);
  }

  const [showPreview, setShowPreview] = useState(true);
  const value = useWatch({ name });

  useEffect(
    () =>
      listenFocusFormInput((event) => {
        setShowPreview(event.detail.name !== name);
      }),
    [name],
  );

  const renderValue = () => (render ? render(value) : value);

  return enabled && showPreview ? (
    <PreviewWrapper
      $size={size}
      $previewOnly={previewOnly}
      $disabled={disabled}
      onFocus={() => {
        if (!previewOnly && !disabled) {
          setShowPreview(false);
        }
      }}
      tabIndex={0}
      id={`${id}-preview`}
      className={classNames.join(' ')}
      {...rest}
    >
      {ellipsis ? <TextEllipsis>{renderValue()}</TextEllipsis> : renderValue()}
    </PreviewWrapper>
  ) : (
    <InputWrapper
      $size={size}
      onBlur={(event: FocusEvent) => {
        /**
         * Blur only if the focus moves outside this cell.
         * This is necessary for DateRangePicker which encompasses multiple inputs and focus/blur may happen "internally" between them.
         */
        const datePickerPopups = document.querySelectorAll(
          `.${FORM_DATE_PICKER_POPUP_CLASSNAME}`,
        );
        const isFocusInsideDatePickerPopup = Array.from(datePickerPopups).some(
          (popup) => isContained(event.relatedTarget, popup),
        );
        if (
          !isContained(event.relatedTarget, event.currentTarget) &&
          !isFocusInsideDatePickerPopup
        ) {
          // Little delay to ensure that the input onChange has happened before the preview opened.
          setTimeout(() => setShowPreview(true), 100);
        }
      }}
    >
      {children}
    </InputWrapper>
  );
};

const isContained = (element: Element | null, container: Element | null) => {
  let parent = element;

  while (parent) {
    if (parent === container) {
      return true;
    }
    parent = parent.parentElement;
  }

  return false;
};

const PreviewWrapper = styled.div<{
  $size: SizeType;
  $previewOnly: boolean;
  $disabled?: boolean;
}>`
  cursor: text;
  border: 1px solid transparent;
  transition: 0.2s border;
  padding-block: ${({ $size }) =>
    $size === 'small'
      ? inputPaddingBlockSM
      : $size === 'large'
        ? inputPaddingBlockLG
        : inputPaddingBlock}px;
  padding-inline: ${({ $size }) =>
    $size === 'small'
      ? inputPaddingInlineSM
      : $size === 'large'
        ? inputPaddingInlineLG
        : inputPaddingInline}px;
  border-radius: ${({ $size }) =>
    $size === 'small'
      ? token.borderRadiusSM
      : $size === 'large'
        ? token.borderRadiusLG
        : token.borderRadius}px;

  // When ellipsis is not enabled and value is empty then height would be zero without this
  min-height: ${({ $size }) =>
    (($size === 'small'
      ? inputPaddingBlockSM
      : $size === 'large'
        ? inputPaddingBlockLG
        : inputPaddingBlock) +
      1) *
      2 +
    fontSize * lineHeight}px;

  td > &,
  td > .form-field > & {
    margin-block: -${({ $size }) =>
        ($size === 'small' ? inputPaddingBlockSM : inputPaddingBlock) +
        // 1px for the border:
        1}px;
    margin-inline: -4px;
    border-radius: ${token.borderRadiusSM}px;
  }

  &,
  .ant-typography {
    font-size: ${fontSize}px;
    line-height: ${lineHeight};
  }

  ${({ $previewOnly }) =>
    $previewOnly === true
      ? ''
      : `
  &:hover {
    border-color: ${colorPrimary};
  }`};

  ${({ $disabled }) =>
    $disabled
      ? `
  &:hover {
    cursor: not-allowed;
    background: ${colorBorderDisabled};
    color: ${colorTextQuaternary};
    border-color: ${colorBorderSecondary};
  }`
      : ''};
`;

const InputWrapper = styled.div<{
  $size: SizeType;
}>`
  min-width: 100%;
  display: flex;

  td > &,
  td > .form-field > & {
    margin-block: -${({ $size }) =>
        ($size === 'small' ? inputPaddingBlockSM : inputPaddingBlock) +
        // 1px for the border:
        1}px;
    margin-inline: -4px;
  }

  .ant-input,
  .ant-input-number,
  .ant-select,
  .ant-picker {
    width: 100%;
  }
  .ant-select:focus-within .ant-select-selector {
    border-color: ${colorLink};
  }
`;
